import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { NIP_ROWS, EMPTY_NUTRITION, computeNipRows } from "@/lib/nutrition";
import { NutritionPanel } from "@/components/NutritionPanel";

/**
 * Edits the FSANZ Nutrition Information Panel for a retail product. Values
 * are entered PER 100g/100mL; the per-serving column is derived from the
 * serving size. Live preview shows the compliant two-column panel.
 */
export function NutritionEditor({ value, onChange }) {
  const nut = { ...EMPTY_NUTRITION, ...(value || {}) };
  const set = (patch) => onChange({ ...nut, ...patch });

  return (
    <div className="space-y-3 surface-alt p-3 rounded-lg" data-testid="nutrition-editor">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-sm font-semibold">Nutrition Information Panel</Label>
          <p className="text-[11px] text-charcoal-soft">FSANZ NIP — enter values per 100g/100mL; per-serve is auto-calculated.</p>
        </div>
        <Switch
          data-testid="nip-enabled"
          checked={!!nut.enabled}
          onCheckedChange={(v) => set({ enabled: v })}
        />
      </div>

      {nut.enabled && (
        <>
          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Servings / pack</Label>
              <Input data-testid="nip-servings" value={nut.servings_per_pack}
                onChange={(e) => set({ servings_per_pack: e.target.value })} placeholder="8" className="h-9" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Serving size (label)</Label>
              <Input data-testid="nip-serving-size" value={nut.serving_size}
                onChange={(e) => set({ serving_size: e.target.value })} placeholder="1 slice (60g)" className="h-9" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Serving size (g)</Label>
              <Input data-testid="nip-serving-g" type="number" value={nut.serving_size_g ?? ""}
                onChange={(e) => set({ serving_size_g: e.target.value })} placeholder="60" className="h-9" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            {NIP_ROWS.map((r) => (
              <div key={r.key} className="space-y-1">
                <Label className="text-xs">{r.label} ({r.unit}) / 100g</Label>
                <Input
                  data-testid={`nip-${r.key}`}
                  type="number"
                  value={nut[r.key] ?? ""}
                  onChange={(e) => set({ [r.key]: e.target.value })}
                  placeholder="0"
                  className="h-9"
                />
              </div>
            ))}
          </div>

          <div>
            <Label className="text-xs text-charcoal-soft">Preview</Label>
            <NutritionPanel rows={computeNipRows(nut)} nutrition={nut} />
          </div>
        </>
      )}
    </div>
  );
}
